// Dibujo de la historia de Instagram (JPG 1080x1920) con canvas, sin React.
// Orden: foto -> capa-fondo.png -> capa-frente.png -> seis textos blancos (Ficha 20, §8).
// Las capas salen del PSD con scripts/extraer-historia.py. La tipografía es Korolev
// Condensed del kit web de Adobe Fonts: la hoja se agrega a la página solo cuando se
// pide una historia (cargarKit), para que el sitio público no pida esas fuentes.

export interface DatosHistoria {
  fecha: string; // YYYY-MM-DD
  nombre: string;
  direccion: string;
  telefono: string;
}

const ANCHO = 1080;
const ALTO = 1920;
// Familias del kit. Los pesos del kit no siguen los nombres del PSD: se ajustan acá
// (Medium del PSD -> PESO_MEDIO, Bold del PSD -> PESO_NEGRITA) mirando la referencia.
const KIT_URL = "https://use.typekit.net/vzw6yom.css";
const FAMILIAS = '"korolev-condensed-1", "korolev-condensed"';
const PESO_MEDIO = 500;
const PESO_NEGRITA = 700;
// Sombra de las capas «DOM 01» y «LUN 02» del PSD: negra, ángulo 135°, 6 px, sin desenfoque.
const SOMBRA_DISTANCIA = 6;
const SOMBRA_OPACIDAD = 0.1;

const DIAS3 = ["DOM", "LUN", "MAR", "MIE", "JUE", "VIE", "SAB"];
const DIAS_COMPLETO = ["DOMINGO", "LUNES", "MARTES", "MIERCOLES", "JUEVES", "VIERNES", "SABADO"];

interface Texto {
  contenido: string;
  peso: number;
  tamano: number;
  alinear: "left" | "center";
  x: number;
  y: number; // línea base
  xMax?: number; // el texto no puede pasar de acá
  sombra?: boolean;
}

function sinTildes(s: string): string {
  return s.normalize("NFD").replace(/[̀-ͯ]/g, "");
}

function cargarImagen(origen: string | Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = typeof origen === "string" ? origen : URL.createObjectURL(origen);
    const img = new Image();
    img.onload = () => {
      if (typeof origen !== "string") URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      if (typeof origen !== "string") URL.revokeObjectURL(url);
      reject(new Error(typeof origen === "string" ? `No se pudo cargar ${origen}.` : "No se pudo leer la foto de la historia."));
    };
    img.src = url;
  });
}

function cargarKit(): Promise<void> {
  const existente = document.querySelector<HTMLLinkElement>("link[data-kit-historias]");
  if (existente?.sheet) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const link = existente ?? document.createElement("link");
    link.addEventListener("load", () => resolve(), { once: true });
    link.addEventListener("error", () => reject(new Error("No cargaron las fuentes de la historia.")), { once: true });
    if (!existente) {
      link.rel = "stylesheet";
      link.href = KIT_URL;
      link.dataset.kitHistorias = "";
      document.head.appendChild(link);
    }
  });
}

async function esperarFuentes(): Promise<void> {
  try {
    await cargarKit();
    const grupos = await Promise.all(
      [PESO_MEDIO, PESO_NEGRITA].map(async (peso) => {
        const caras = await Promise.all(
          ["korolev-condensed-1", "korolev-condensed"].map((f) => document.fonts.load(`${peso} 50px "${f}"`, "AaÑ0123456789")),
        );
        return caras.flat().length;
      }),
    );
    if (grupos.some((n) => n === 0)) throw new Error("sin caras");
  } catch {
    throw new Error("No cargaron las fuentes de la historia.");
  }
}

function dibujarCover(ctx: CanvasRenderingContext2D, img: HTMLImageElement): void {
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  if (w === ANCHO && h === ALTO) {
    ctx.drawImage(img, 0, 0);
    return;
  }
  const escala = Math.max(ANCHO / w, ALTO / h);
  const dw = w * escala;
  const dh = h * escala;
  ctx.drawImage(img, (ANCHO - dw) / 2, (ALTO - dh) / 2, dw, dh);
}

function dibujarTexto(ctx: CanvasRenderingContext2D, t: Texto): void {
  let tamano = t.tamano;
  const fuente = (px: number) => `${t.peso} ${px}px ${FAMILIAS}`;
  ctx.font = fuente(tamano);
  if (t.xMax !== undefined) {
    while (tamano > 30 && t.x + ctx.measureText(t.contenido).width > t.xMax) {
      tamano -= 1;
      ctx.font = fuente(tamano);
    }
  }
  // Si ni al mínimo entra, se corta con «…» antes que pisar el dato de al lado
  let contenido = t.contenido;
  if (t.xMax !== undefined && t.x + ctx.measureText(contenido).width > t.xMax) {
    while (contenido.length > 1 && t.x + ctx.measureText(`${contenido}…`).width > t.xMax) contenido = contenido.slice(0, -1);
    contenido = `${contenido.trimEnd()}…`;
  }
  ctx.textAlign = t.alinear;
  ctx.textBaseline = "alphabetic";
  if (t.sombra) {
    const d = SOMBRA_DISTANCIA * Math.SQRT1_2;
    ctx.fillStyle = `rgba(0, 0, 0, ${SOMBRA_OPACIDAD})`;
    ctx.fillText(contenido, t.x + d, t.y + d);
  }
  ctx.fillStyle = "#ffffff";
  ctx.fillText(contenido, t.x, t.y);
}

export async function generarHistoria(datos: DatosHistoria, foto: Blob): Promise<{ blob: Blob; nombreArchivo: string }> {
  const [y, m, d] = datos.fecha.split("-").map(Number);
  const inicio = new Date(y, m - 1, d);
  const fin = new Date(y, m - 1, d + 1);
  const dia2 = (f: Date) => String(f.getDate()).padStart(2, "0");
  const dow3Inicio = DIAS3[inicio.getDay()];

  const nombre = sinTildes(datos.nombre).toUpperCase().replace(/^FARMACIA\s+/, "").trim();

  const [imgFoto, imgFondo, imgFrente] = await Promise.all([
    cargarImagen(foto),
    cargarImagen("/historias/capa-fondo.png"),
    cargarImagen("/historias/capa-frente.png"),
  ]);
  await esperarFuentes();

  const canvas = document.createElement("canvas");
  canvas.width = ANCHO;
  canvas.height = ALTO;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("No se pudo preparar el dibujo de la historia.");

  dibujarCover(ctx, imgFoto);
  ctx.drawImage(imgFondo, 0, 0);
  ctx.drawImage(imgFrente, 0, 0);

  const textos: Texto[] = [
    { contenido: nombre, peso: PESO_NEGRITA, tamano: 50, alinear: "left", x: 193, y: 1337, xMax: 520 },
    { contenido: `${dow3Inicio} ${dia2(inicio)}`, peso: PESO_MEDIO, tamano: 77, alinear: "center", x: 277, y: 1513, sombra: true },
    { contenido: `${DIAS3[fin.getDay()]} ${dia2(fin)}`, peso: PESO_MEDIO, tamano: 77, alinear: "center", x: 798, y: 1513, sombra: true },
    { contenido: DIAS_COMPLETO[fin.getDay()], peso: PESO_NEGRITA, tamano: 33, alinear: "center", x: 587, y: 1527 },
    { contenido: datos.direccion, peso: PESO_MEDIO, tamano: 50, alinear: "left", x: 234, y: 1740, xMax: 620 },
    { contenido: datos.telefono, peso: PESO_MEDIO, tamano: 50, alinear: "left", x: 703, y: 1740, xMax: 960 },
  ];
  for (const t of textos) dibujarTexto(ctx, t);

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("No se pudo armar el JPG de la historia."))), "image/jpeg", 0.92);
  });
  const nombreArchivo = `${dia2(inicio)}_${dow3Inicio}_${nombre}.jpg`.replace(/\s+/g, "_");
  return { blob, nombreArchivo };
}
